<!-- The wrapper exists only to be the positioning context for the PAID IN FULL watermark below.
     The overlay has to be a SIBLING of groundworks-card: every ng-content in the library's card
     template is select-ed (header / body / actions), so any non-matching child of groundworks-card is
     silently dropped and never renders. A sibling is not projected content, so the select gate never
     applies and no ::ng-deep is needed.

     It carries h-100 and NOT d-flex. groundworks-card's host element is display:inline and an inline
     box is not a block container, so this wrapper — not the host — is the inner .card's containing
     block; separately, the wrapper's own h-100 resolves past the inline hosts above it to the viewer's
     grid cell, which has a definite height as a stretched flex item.

     Making this wrapper a flex container blockifies that host into a flex item and breaks the chain.
     Measured in a viewer cell 600px tall and 741px wide: with `d-flex` the card's WIDTH collapses to
     its content (741px -> 38px), and with `d-flex flex-column` its HEIGHT collapses instead
     (600px -> 26px) — the latter is what would leave the shorter card in a row unequal. -->
<div class="h-100 position-relative paid-in-full-card-wrapper" data-testid="project-card-wrapper">
<groundworks-card [cardClasses]="['h-100']">
  <groundworks-card-header>
    <ng-template groundworksCardHeaderTitle>
      <div class="d-flex flex-column flex-grow-1">
        <div class="d-flex align-items-center gap-2">
          <h2 class="card-title h4 m-0">{{ headerTitle() }}</h2>
          <span class="text-muted" data-testid="project-bc-id">{{ headerBcProjectId() }}</span>
          @if (projectCount() > 1) {
          <span class="ms-auto text-muted" data-testid="project-position-label">{{ positionLabel() }}</span>
          }
        </div>
        @if (projectStatusBadge() || invoiceStatusBadge() || isPaidInFull()) {
        <div class="d-flex align-items-start gap-2 mt-1">
          @if (projectStatusBadge(); as badge) {
          <div class="d-flex flex-column align-items-center">
            <groundworks-badge data-testid="project-status-badge" [label]="badge.label" [type]="badge.type ?? 'primary'"
              [size]="badge.size ?? 'normal'" [icon]="badge.icon ?? ''" />
            @if (badge.date; as badgeDate) {
            <span class="text-muted small" data-testid="project-status-badge-date">{{ badgeDate | date:'MM/dd/yyyy':'UTC' }}</span>
            }
          </div>
          }
          @if (invoiceStatusBadge(); as badge) {
          <div class="d-flex flex-column align-items-center">
            <groundworks-badge data-testid="project-invoice-status-badge" [label]="badge.label"
              [type]="badge.type ?? 'primary'" [size]="badge.size ?? 'normal'" [icon]="badge.icon ?? ''" />
            @if (badge.date; as badgeDate) {
            <span class="text-muted small" data-testid="project-invoice-status-badge-date">{{ badgeDate | date:'MM/dd/yyyy':'UTC' }}</span>
            }
          </div>
          }
          @if (isPaidInFull()) {
          <span class="visually-hidden" data-testid="project-paid-in-full-sr-text">This project is paid in full.</span>
          }
        </div>
        }
      </div>
    </ng-template>
  </groundworks-card-header>
  <groundworks-card-body>
    <ul class="nav nav-tabs mb-3">
      <li class="nav-item">
        <button type="button" class="nav-link" [class.active]="compensationTab() === 'project'"
          (click)="compensationTab.set('project')">Project</button>
      </li>
      @if (enablePpj) {
      <li class="nav-item">
        <button type="button" class="nav-link" [class.active]="compensationTab() === 'ppj'"
          (click)="compensationTab.set('ppj')">Pay Per Job</button>
      </li>
      }
      @if (enableCommissions) {
      <li class="nav-item">
        <button type="button" class="nav-link" [class.active]="compensationTab() === 'commissions'"
          (click)="compensationTab.set('commissions')">Commissions</button>
      </li>
      }
    </ul>
    <div [hidden]="compensationTab() !== 'project'">
    <div class="mb-4">
      @if (projectTableData(); as tableData) {
      <div class="table-responsive">
        <groundworks-table [data]="tableData" />
      </div>
      }
    </div>
    @if (projectDatesTableData(); as datesData) {
    <div class="mb-4 table-responsive">
      <groundworks-table [data]="datesData" />
    </div>
    }
    @if (!isNonInstallable()) {
    <groundworks-card #reviewCard [isCollapsible]="true" [collapsibleVariant]="'box'" [cardClasses]="['mb-4']"
      [initialCollapsed]="true">
      <groundworks-card-header [isCollapsible]="true" [collapsibleVariant]="'box'">
        <ng-template groundworksCardHeaderTitle>
          <div class="grid w-100">
            <div class="g-col-6">
              <h5 class="card-title h5 m-0">Review@if (reviewCheckpoint()?.dueDate) {<span class="text-danger">*</span>}</h5>
            </div>
            @if (reviewCheckpoint()?.dueDate) {
            <div class="g-col-6">
              <span class="h5 m-0 fw-normal">Review by: {{ reviewCheckpoint()!.dueDate | date:'MM/dd/yyyy':'UTC' }}</span>
            </div>
            }
          </div>
        </ng-template>
      </groundworks-card-header>
      <groundworks-card-body>
        @if (reviewCheckpoint(); as checkpoint) {
        <div class="form-check d-flex align-items-center gap-2">
          <input type="checkbox" class="form-check-input" [id]="'checkpoint-' + checkpoint.id"
            [checked]="!!checkpoint.completedAt"
            [disabled]="!!checkpoint.completedAt || isReadOnly() || isCheckpointDisabled() || isCheckpointCompleting().has(checkpoint.id)"
            (change)="onCompleteCheckpoint(checkpoint)" />
          <label class="form-check-label" [for]="'checkpoint-' + checkpoint.id">
            Mark Project Reviewed
          </label>
          @if (checkpoint.completedAt) {
          <span class="text-muted ms-2">
            {{ checkpoint.completedByName }} {{ checkpoint.completedAt | date:'h:mm a MM/dd/yyyy' }}
          </span>
          }
        </div>
        } @else {
        <p class="text-muted mb-0">Schedule project to complete review</p>
        }
      </groundworks-card-body>
    </groundworks-card>
    <groundworks-card #confirmCard [isCollapsible]="true" [collapsibleVariant]="'box'" [cardClasses]="['mb-4']"
      [initialCollapsed]="true">
      <groundworks-card-header [isCollapsible]="true" [collapsibleVariant]="'box'">
        <ng-template groundworksCardHeaderTitle>
          <div class="grid w-100">
            <div class="g-col-6">
              <h5 class="card-title h5 m-0">Confirm@if (confirmationCheckpoint()?.dueDate) {<span class="text-danger">*</span>}</h5>
            </div>
            @if (confirmationCheckpoint()?.dueDate) {
            <div class="g-col-6">
              <span class="h5 m-0 fw-normal">Confirm by: {{ confirmationCheckpoint()!.dueDate | date:'MM/dd/yyyy':'UTC' }}</span>
            </div>
            }
          </div>
        </ng-template>
      </groundworks-card-header>
      <groundworks-card-body>
        @if (confirmationCheckpoint(); as checkpoint) {
        <div class="form-check d-flex align-items-center gap-2">
          <input type="checkbox" class="form-check-input" [id]="'checkpoint-' + checkpoint.id"
            [checked]="!!checkpoint.completedAt"
            [disabled]="!!checkpoint.completedAt || isReadOnly() || isCheckpointDisabled() || isCheckpointCompleting().has(checkpoint.id)"
            (change)="onCompleteCheckpoint(checkpoint)" />
          <label class="form-check-label" [for]="'checkpoint-' + checkpoint.id">
            Mark Project Confirmed
          </label>
          @if (checkpoint.completedAt) {
          <span class="text-muted ms-2">
            {{ checkpoint.completedByName }} {{ checkpoint.completedAt | date:'h:mm a MM/dd/yyyy' }}
          </span>
          }
        </div>
        } @else {
        <p class="text-muted mb-0">Schedule project to complete confirmation</p>
        }
      </groundworks-card-body>
    </groundworks-card>
    <groundworks-card [isCollapsible]="true" [collapsibleVariant]="'box'" [cardClasses]="['mb-4']"
      [initialCollapsed]="true">
      <groundworks-card-header [isCollapsible]="true" [collapsibleVariant]="'box'">
        <ng-template groundworksCardHeaderTitle>
          <div class="grid w-100">
            <div class="g-col-6">
              <h5 class="card-title h5 m-0">Utilities@if (project().utilitiesRequired && !utilitiesNotNeeded()) {<span class="text-danger">*</span>}</h5>
            </div>
            @if (utilitiesCheckpoint()?.dueDate && !utilitiesNotNeeded()) {
            <div class="g-col-6">
              <span class="h5 m-0 fw-normal">Due: {{ utilitiesCheckpoint()!.dueDate | date:'MM/dd/yyyy':'UTC' }}</span>
            </div>
            }
          </div>
        </ng-template>
      </groundworks-card-header>
      <groundworks-card-body>
        @if (project().utilitiesRequired) {
        <div class="form-check form-switch mb-3">
          <input type="checkbox" class="form-check-input" [id]="'utilities-not-needed-' + project().id"
            [checked]="utilitiesNotNeeded()"
            [disabled]="isProjectCancelled() || isUtilitiesNotNeededUpdating() || startDatePassed() || hasUtilities()"
            (change)="onToggleUtilitiesNotNeeded($any($event.target).checked)" />
          <label class="form-check-label" [for]="'utilities-not-needed-' + project().id">
            Utilities not needed
          </label>
        </div>
        }
        @if (utilitiesTableData(); as tableData) {
        <div class="table-responsive mb-3">
          <groundworks-table [data]="tableData" />
        </div>
        }
      </groundworks-card-body>
      <groundworks-card-actions [actions]="utilityActions()" />
    </groundworks-card>
    @if (configService.enablePermitting && permitsLoaded()) {
    <groundworks-card [isCollapsible]="true" [collapsibleVariant]="'box'" [cardClasses]="['mb-4']"
                      [initialCollapsed]="!shouldAutoExpandPermits()">
      <groundworks-card-header [isCollapsible]="true" [collapsibleVariant]="'box'"
                               [class.bg-warning-subtle]="permitsHighlight() === 'yellow'"
                               [class.bg-danger-subtle]="permitsHighlight() === 'red'">
        <ng-template groundworksCardHeaderTitle>
          <h5 class="card-title h5 m-0">Permits</h5>
        </ng-template>
      </groundworks-card-header>
      <groundworks-card-body>
        @if (permitsLoadError(); as err) {
          <div class="alert alert-danger mb-3" role="alert">{{ err }}</div>
        }
        @if (permitsTableData(); as tableData) {
          <div class="table-responsive mb-3">
            <groundworks-table [data]="tableData" />
          </div>
        }
      </groundworks-card-body>
      <groundworks-card-actions [actions]="permitsActions()" />
    </groundworks-card>
    }
    <groundworks-card [isCollapsible]="true" [collapsibleVariant]="'box'" [cardClasses]="['mb-4']"
      [initialCollapsed]="true">
      <groundworks-card-header [isCollapsible]="true" [collapsibleVariant]="'box'">
        <ng-template groundworksCardHeaderTitle>
          <div class="grid w-100">
            <div class="g-col-6">
              <h5 class="card-title h5 m-0">Special Needs</h5>
            </div>
            @if (soonestSpecialNeedDueDate(); as dueDate) {
            <div class="g-col-6">
              <span class="h5 m-0 fw-normal">Due: {{ dueDate | date:'MM/dd/yyyy':'UTC' }}</span>
            </div>
            }
          </div>
        </ng-template>
      </groundworks-card-header>
      <groundworks-card-body>
        @if (specialNeedsTableData(); as tableData) {
        <div class="table-responsive mb-3">
          <groundworks-table [data]="tableData" />
        </div>
        }
      </groundworks-card-body>
      <groundworks-card-actions [actions]="specialNeedActions()" />
    </groundworks-card>
    }
    </div>
    @if (enableCommissions || enablePpj) {
    <app-compensation-tabs [projectId]="project().id"
      [branchId]="branchId()"
      [percentProjectPaid]="percentProjectPaid()"
      [acr]="acr()"
      [forcedView]="compensationForcedView()"
      [enableCommissions]="enableCommissions"
      [enablePpj]="enablePpj"
      [isNonInstallable]="isNonInstallable()"
      [hidden]="compensationTab() === 'project'" />
    }
  </groundworks-card-body>
  @if (configService.showJobActions && compensationTab() === 'project') {
  <groundworks-card-actions [actions]="projectActions" />
  }
  @if (compensationTab() === 'commissions' && commissionFooterActions().length > 0) {
  <groundworks-card-actions [actions]="commissionFooterActions()" data-testid="commissions-card-actions" />
  }
  @if (compensationTab() === 'ppj' && ppjFooterActions().length > 0) {
  <groundworks-card-actions [actions]="ppjFooterActions()" data-testid="ppj-card-actions" />
  }
</groundworks-card>
@if (isPaidInFull()) {
<div class="paid-in-full-overlay" data-testid="project-paid-in-full-overlay" aria-hidden="true">
  <span class="paid-in-full-stamp" data-testid="project-paid-in-full-stamp">
    <span class="d-block text-nowrap" data-testid="project-paid-in-full-line">Paid in</span>
    <span class="d-block text-nowrap" data-testid="project-paid-in-full-line">Full</span>
  </span>
</div>
}
</div>
